import React from "react";
import { ExternalLink } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";

const CATEGORY_LABEL = {
    alojamiento: "Alojamiento",
    evento: "Evento",
    actividad: "Actividad",
    gastronomia: "Gastronomía",
    cultura: "Cultura",
    otro: "Turismo",
};

function timeAgo(iso) {
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 3600) return `hace ${Math.max(1, Math.floor(diff / 60))} min`;
        if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
        return `hace ${Math.floor(diff / 86400)} días`;
    } catch {
        return "";
    }
}

export default function TarifaNews() {
    const { data: items, loading } = useFreshData(
        async () => {
            const r = await api.get("/tarifa-news", { params: { limit: 6 } });
            return r.data || [];
        },
        { initialData: [] }
    );

    if (!loading && (!items || items.length === 0)) return null;

    return (
        <section
            id="tarifa-medios"
            data-testid="tarifa-news-section"
            className="bg-white border-b border-[#E2E4E9]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                            Noticias al día
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Noticias al día.
                        </h2>
                        <p className="mt-4 text-[#4A5568] text-lg">
                            Recopilamos las noticias más recientes que benefician al
                            sector turístico de Tarifa: alojamiento, eventos,
                            actividades, playas, gastronomía y cultura.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div data-testid="tarifa-news-loading" className="text-[#4A5568] py-8">
                        Buscando noticias...
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((n, i) => (
                            <a
                                key={n.id || n.link}
                                href={n.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`tarifa-news-item-${i}`}
                                className="border border-[#E2E4E9] hover:border-[#082050] transition-colors p-6 flex flex-col group"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-[10px] uppercase tracking-widest text-[#082050]/60">
                                        {CATEGORY_LABEL[n.category] || "Turismo"}
                                    </span>
                                    <span className="text-[10px] text-[#718096]">
                                        {timeAgo(n.fetched_at)}
                                    </span>
                                </div>
                                <h3 className="mt-3 text-base font-semibold text-[#082050] tracking-tight leading-snug line-clamp-3">
                                    {n.title}
                                </h3>
                                {n.summary && (
                                    <p className="mt-3 text-sm text-[#4A5568] leading-relaxed line-clamp-4">
                                        {n.summary}
                                    </p>
                                )}
                                <div className="mt-6 pt-4 border-t border-[#E2E4E9] flex items-center justify-between text-xs">
                                    <span className="text-[#718096] truncate max-w-[70%]">
                                        {n.source || "Fuente externa"}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[#082050] font-medium">
                                        Ver artículo <ExternalLink size={11} />
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
